import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { SuiContainer, SuiDropdown, SuiDropdownOption } from '@splashthat/sui';

import { INPUT_TYPE_SELECT, INPUT_TYPE_CHECKBOX } from 'common/entities/formNode/inputTypes';
import { getAdditionalGuestCountQuestion, getFormNodeOptions } from 'common/entities/formNode/selectors';

import RichTextLabelControl from './components/RichTextLabelControl';
import ReservedNodeIndication from './components/ReservedNodeIndication';
import EditViewSectionContainer from './EditViewSectionContainer';
import { getAllow20AdditionalGuest, getFormId } from '../../selectors';
import { updateMaxGuestCountNode } from '../../actions/additionalGuestActions';
import { ADDITIONAL_GUEST_COUNT_LABEL } from '../../constants/reservedInputNameLabels';
import SectionSeparator from './components/SectionSeparator';

export const GUEST_COUNT_LABEL = 'Maximum Number of Guests Allowed';
const QUESTION_TYPE_LABEL = 'Question Type';
const CHECKBOX_LABEL = 'Checkbox';
const SELECT_LABEL = 'Dropdown';
const TOOLTIP_CONTENT = `Additional guests will appear on the guest list.
Add the "Guest of" column to quickly see who invited them.`;

const getGuestCountArray = (allow20AdditionalGuests) => {
    const MAX_GUEST_COUNT = allow20AdditionalGuests ? 21 : 10;
    return Array.from(new Array(MAX_GUEST_COUNT - 1), (val, index) => index + 1);
};

/**
 * Component for editing additional guest count and label question.
 */
class EditQuestionAdditionalGuestCount extends Component {
    static propTypes = {
        maxGuestCount: PropTypes.number.isRequired,
        updateMaxGuestCountNode: PropTypes.func.isRequired,
        /** Node id of the additional guest count question.
         * Note that this is the edit view of the additional guest group */
        additionalGuestCountQuestionNodeId: PropTypes.string.isRequired,
        additionalGuestCountQuestionInputType: PropTypes.string.isRequired,
        allow20AdditionalGuests: PropTypes.bool.isRequired,
    };

    handleChangeMaxGuestCount = (event, value) => {
        const nodeId = this.props.additionalGuestCountQuestionNodeId;
        const maxGuestCount = parseInt(value, 10);
        const newInputType = maxGuestCount > 1 ? INPUT_TYPE_SELECT : this.props.additionalGuestCountQuestionInputType;
        this.props.updateMaxGuestCountNode(nodeId, maxGuestCount, newInputType);
    };

    handleChangeQuestionType = (event, value) => {
        this.props.updateMaxGuestCountNode(this.props.additionalGuestCountQuestionNodeId, 1, value);
    };

    render() {
        return (
            <EditViewSectionContainer>
                <SuiContainer variation="flex-group">
                    <RichTextLabelControl formNodeId={this.props.additionalGuestCountQuestionNodeId} />
                    <ReservedNodeIndication label={ADDITIONAL_GUEST_COUNT_LABEL} tooltip={TOOLTIP_CONTENT} />
                </SuiContainer>

                <SectionSeparator />

                <SuiContainer variation="flex-group">
                    <SuiDropdown
                        label={GUEST_COUNT_LABEL}
                        value={this.props.maxGuestCount}
                        onChange={this.handleChangeMaxGuestCount}
                        fullWidth
                    >
                        {getGuestCountArray(this.props.allow20AdditionalGuests).map((guestCount) => (
                            <SuiDropdownOption
                                key={guestCount}
                                value={guestCount}
                                displayValue={guestCount}
                                data-qa={`additional-guest-option-${guestCount}`}
                            />
                        ))}
                    </SuiDropdown>
                </SuiContainer>

                <SuiContainer variation="flex-group">
                    <SuiDropdown
                        label={QUESTION_TYPE_LABEL}
                        value={this.props.additionalGuestCountQuestionInputType}
                        onChange={this.handleChangeQuestionType}
                        fullWidth
                    >
                        <SuiDropdownOption value={INPUT_TYPE_CHECKBOX} displayValue={CHECKBOX_LABEL} />
                        <SuiDropdownOption value={INPUT_TYPE_SELECT} displayValue={SELECT_LABEL} />
                    </SuiDropdown>
                </SuiContainer>
            </EditViewSectionContainer>
        );
    }
}

const mapStateToProps = (state) => {
    const formId = getFormId(state);
    const countQuestionNode = getAdditionalGuestCountQuestion(state, formId);

    const additionalGuestCountQuestionNodeId = get(countQuestionNode, 'id');
    const additionalGuestCountQuestionInputType = get(countQuestionNode, 'inputType');

    const countQuestionOptions = getFormNodeOptions(state, additionalGuestCountQuestionNodeId);
    const maxGuestCount = ((countQuestionOptions || []).length || 2) - 1;

    const allow20AdditionalGuests = getAllow20AdditionalGuest(state);

    return {
        maxGuestCount,
        additionalGuestCountQuestionInputType,
        additionalGuestCountQuestionNodeId,
        allow20AdditionalGuests,
    };
};

const mapDispatchToProps = {
    updateMaxGuestCountNode,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditQuestionAdditionalGuestCount);
